import React, { useState, useEffect, useMemo, useCallback } from 'react';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { getNow, toISODate } from '@libs/NwMoment';

import { useUserPermission } from '@contexts/UserContext';

import { useContact } from '@hooks/API/Contact/UseContact';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import EventDrawer from '@components/EventDrawer/EventDrawer';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwIcon from '@components/Gui/NwIcon';
import AgendaContainer from '@components/Schedule/Agenda/AgendaContainer';

import { AgendaProvider } from './AgendaContext';
import MobileAgendaContainer from './MobileAgendaContainer';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const LockContainer = styled(FlexContainer)`
    min-height: 320px;
    padding: 16px 0;
    svg {
        font-size: ${(props) => (props.$previewMode ? '80px' : '120px')};
    }
`;

const ScheduleMobileWrapper = styled.div`
    min-height: 100vh;
    padding: 0.25rem;
    background: white;
`;

const ModelDrawerAgenda = ({ type, contactId, defaultDate = getNow() }) => {
    const breakpoints = useNwBreakPoints();
    const isMobile = breakpoints === 1;

    const [selectedEvent, setSelectedEvent] = useState(null);

    let defaultSchedulePeriod = { start: toISODate(defaultDate), end: toISODate(defaultDate) };
    const [scheduleStartRange, setScheduleStartRange] = useState(defaultSchedulePeriod.start);
    const [scheduleEndRange, setScheduleEndRange] = useState(defaultSchedulePeriod.end);
    const [scheduleRange, setScheduleRange] = useState(null);

    useEffect(() => {
        if (scheduleStartRange && scheduleEndRange) {
            const dateObject = { start: getMoment(scheduleStartRange), end: getMoment(scheduleEndRange) };
            setScheduleRange(dateObject);
        }
    }, [scheduleStartRange, scheduleEndRange]);

    const onScheduleRangeChange = useCallback((start, end) => {
        //start and end provided as js date objects
        const startmoment = getMoment(start);
        const endmoment = getMoment(end);
        setScheduleStartRange(toISODate(startmoment));
        setScheduleEndRange(toISODate(endmoment));
    }, []);

    const [params, setParams] = useState({
        inclJobs: true,
        inclExtJobs: true,
        inclCastings: true,
        checkConflict: false,
        inclTravels: true,
        inclAccommodations: true,
        inclExternalJobs: true,
        inclFollowUps: true,
        inclMeetings: true,
        inclNotices: true,
        General: false,
        Warning: false,
        idmodels: [contactId],
    });

    const { data: currentContact } = useContact(contactId);
    const viewModelCalendar = useUserPermission(
        'ViewModelCalendar',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewCalendar = type === 'model' ? viewModelCalendar : true;

    useEffect(() => {
        setParams({
            ...params,
            idmodels: [contactId],
        });
    }, [contactId]);

    const afterReload = useCallback(() => {
        //do nothing
    }, []);
    const handleClearContactId = useCallback(() => {
        //do nothing
    }, []);
    const bookingSection = false;
    const forceReload = false;
    const onSelectEvent = useCallback((event) => {
        setSelectedEvent(event);
    }, []);
    const previewMode = false;

    //ADD A BUTTON LATER FOR THIS, IN THE CARD HEADER
    // const goToEventSearch = () => {
    //     history({
    //         pathname: '/events-search',
    //         state: {
    //             type: type === 'model' ? 'model'
    //                 : currentContact.SubType === 3 ? 'service' : 'customer',
    //             contactId: contactId
    //         }
    //     });
    // }

    const AgendaContextValues = useMemo(
        () => ({
            afterReload,
            bookingSection,
            contactId,
            forceReload,
            onClearContactID: handleClearContactId,
            onScheduleRangeChange,
            onSelectEvent,
            params,
            previewMode,
            scheduleRange,
            selectedEvent,
            type,
        }),
        [
            afterReload,
            bookingSection,
            contactId,
            forceReload,
            handleClearContactId,
            onSelectEvent,
            onScheduleRangeChange,
            params,
            previewMode,
            scheduleRange,
            selectedEvent,
            type,
        ]
    );

    const getCardMinHeight = () => {
        if (type === 'model') {
            return 580;
        }
        return 520;
    };

    return (
        <>
            <NwCard
                title="Calendar"
                icon={byPrefixAndName.fasl['calendar']}
                minheight={getCardMinHeight()}
                padding="reduced"
            >
                {isMobile ? (
                    <ScheduleMobileWrapper>
                        {viewCalendar ? (
                            scheduleRange ? (
                                <AgendaProvider values={AgendaContextValues}>
                                    <MobileAgendaContainer />
                                </AgendaProvider>
                            ) : (
                                <Loading textBlack small paddingTop="2rem" />
                            )
                        ) : (
                            <LockContainer $previewMode justifyContent="center" alignItems="center">
                                <NwIcon icon={byPrefixAndName.fasl['lock-alt']} />
                            </LockContainer>
                        )}
                    </ScheduleMobileWrapper>
                ) : viewCalendar ? (
                    scheduleRange ? (
                        <AgendaProvider values={AgendaContextValues}>
                            <AgendaContainer />
                        </AgendaProvider>
                    ) : (
                        <Loading />
                    )
                ) : (
                    <LockContainer $previewMode justifyContent="center" alignItems="center">
                        <NwIcon icon={byPrefixAndName.fasl['lock-alt']} />
                    </LockContainer>
                )}
            </NwCard>
            {selectedEvent && (
                <EventDrawer
                    eventType={selectedEvent.TypeName}
                    eventID={selectedEvent.ID}
                    eventObject={selectedEvent}
                    dateSelected={defaultDate}
                    modelId={type === 'model' && contactId}
                    onClose={() => setSelectedEvent(null)}
                    afterUpdate={() => {}}
                    afterRemove={() => {}}
                />
            )}
        </>
    );
};

export default ModelDrawerAgenda;
